import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import Character from '../../../models/Game Layer/character';
import Player from '../../../models/Game Layer/player';
import Session from '../../../models/Game Layer/session';
import Campaign from '../../../models/KB Layer/campaign';
import Universe from '../../../models/KB Layer/universe';
import User, { UserRole } from '../../../models/user';
import { AuthService } from '../../../services/auth-service';
import { GamelayerService } from '../../../services/gamelayer-service';
import { KblayerService } from '../../../services/kblayer-service';
import { UserService } from '../../../services/user-service';
import { LoadingComponent } from '../../loading-component/loading-component';
import { BackButtonComponent } from '../../back-button-component/back-button-component';

type JoinCharacterOption = {
  sessionId: string;
  sessionName: string;
  character: Character;
};

@Component({
  selector: 'app-session-component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    BackButtonComponent,
    LoadingComponent,
  ],
  templateUrl: './session-component.html',
  styleUrl: './session-component.css',
})
export class SessionComponent {
  private readonly gamelayerService = inject(GamelayerService);
  private readonly kblayerService = inject(KblayerService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  readonly auth = inject(AuthService);
  readonly userRole = UserRole;

  readonly sessions = signal<Session[]>([]);
  readonly universes = signal<Universe[]>([]);
  readonly users = signal<User[]>([]);
  readonly isLoadingSessions = signal(true);
  readonly loadSessionsError = signal('');
  readonly loadCampaignsError = signal('');
  readonly loadUsersError = signal('');
  readonly joinSessionError = signal('');
  readonly joiningSessionId = signal<string | null>(null);
  readonly isPreparingJoin = signal(false);
  readonly joinTargetSession = signal<Session | null>(null);
  readonly joinCharacterOptions = signal<JoinCharacterOption[]>([]);
  readonly selectedJoinCharacterId = signal('');

  readonly currentUserId = computed(() => this.auth.currentUser()?.id ?? '');
  readonly visibleSessions = computed(() => {
    const sessions = this.sessions();

    if (this.auth.hasRole(UserRole.DungeonMaster)) {
      const masterId = this.currentUserId();
      return sessions.filter((session) => session.masterId === masterId);
    }

    return sessions;
  });

  ngOnInit(): void {
    this.getSessions();
    this.getUniverses();
    this.getUsers();
  }

  getSessions(): void {
    this.isLoadingSessions.set(true);
    this.loadSessionsError.set('');

    this.gamelayerService.getSessions().subscribe({
      next: (response) => {
        const rawSessions = Array.isArray(response)
          ? response
          : (response as { sessions?: unknown[] })?.sessions ?? [];

        this.sessions.set(rawSessions.map((session) => new Session(session)));
        this.isLoadingSessions.set(false);
      },
      error: (error) => {
        console.error('Error loading sessions:', error);
        this.loadSessionsError.set('The session records could not be loaded right now.');
        this.isLoadingSessions.set(false);
      },
    });
  }

  getUniverses(): void {
    this.loadCampaignsError.set('');

    this.kblayerService.getUniverses().subscribe({
      next: (response) => {
        const rawUniverses = Array.isArray(response) ? response : response?.universes ?? [];

        this.universes.set(rawUniverses.map((universe) => new Universe(universe)));
      },
      error: (error) => {
        console.error('Error loading campaign records:', error);
        this.loadCampaignsError.set(
          'Campaign details could not be loaded, so sessions are showing saved campaign names only.',
        );
      },
    });
  }

  getUsers(): void {
    this.loadUsersError.set('');

    this.userService.getUsers().subscribe({
      next: (response) => {
        const rawUsers = Array.isArray(response) ? response : (response as { users?: unknown[] })?.users ?? [];

        this.users.set(rawUsers.map((user) => new User(user)));
      },
      error: (error) => {
        console.error('Error loading user records:', error);
        this.loadUsersError.set('User names could not be loaded right now.');
      },
    });
  }

  getSessionCampaign(session: Session): Campaign {
    const universe =
      this.universes().find((item) => item.id === session.universeId) ??
      null;
    const campaign =
      universe?.campaigns.find((item) => item.id === session.campaignId) ??
      null;

    return new Campaign({
      id: session.campaignId,
      universeId: session.universeId,
      name: campaign?.name || session.campaignName || 'Unknown Campaign',
      description:
        campaign?.description ||
        session.description ||
        'No description was provided for this campaign.',
    });
  }

  getUserName(userId: string): string {
    const activeUser = this.auth.currentUser();

    if (activeUser?.id === userId) {
      return activeUser.name;
    }

    return this.users().find((user) => user.id === userId)?.name || 'Unknown User';
  }

  hasJoined(session: Session): boolean {
    return session.players.some((player) => player.userId === this.currentUserId());
  }

  isFull(session: Session): boolean {
    return session.maxPlayers > 0 && session.players.length >= session.maxPlayers;
  }

  openJoinSession(session: Session): void {
    const currentUserId = this.currentUserId();
    if (!currentUserId || this.hasJoined(session) || this.isFull(session)) {
      return;
    }

    const sameUniverseSessions = this.visibleSessions().filter(
      (existingSession) => existingSession.universeId === session.universeId,
    );

    if (!sameUniverseSessions.length) {
      this.joinSessionError.set('No matching universe records were found for this session.');
      return;
    }

    this.joinTargetSession.set(session);
    this.joinCharacterOptions.set([]);
    this.selectedJoinCharacterId.set('');
    this.isPreparingJoin.set(true);
    this.joinSessionError.set('');

    forkJoin(sameUniverseSessions.map((existingSession) => this.gamelayerService.getCharacters(existingSession.id))).subscribe({
      next: (charactersBySession) => {
        const options = sameUniverseSessions.flatMap((existingSession, index) => {
          const characters = (charactersBySession[index] ?? []).map(
            (character) => new Character(character),
          );

          return existingSession.players
            .filter((player) => player.userId === currentUserId && !!player.characterId)
            .map((player) => {
              const matchingCharacter = characters.find(
                (character) => character.id === player.characterId,
              );

              if (!matchingCharacter) {
                return null;
              }

              return {
                sessionId: existingSession.id,
                sessionName: existingSession.name,
                character: matchingCharacter,
              };
            })
            .filter((entry): entry is JoinCharacterOption => entry !== null);
        });

        const uniqueOptions = options.filter(
          (option, index, allOptions) =>
            allOptions.findIndex((entry) => entry.character.id === option.character.id) === index,
        );

        this.joinCharacterOptions.set(uniqueOptions);
        this.selectedJoinCharacterId.set(uniqueOptions[0]?.character.id ?? '');
        this.isPreparingJoin.set(false);

        if (!uniqueOptions.length) {
          this.joinSessionError.set(
            'You need a character in this universe that belongs to you before joining this campaign.',
          );
        }
      },
      error: (error) => {
        console.error('Error loading join characters:', error);
        this.joinSessionError.set('Your available characters could not be loaded right now.');
        this.isPreparingJoin.set(false);
      },
    });
  }

  closeJoinSession(): void {
    this.joinTargetSession.set(null);
    this.joinCharacterOptions.set([]);
    this.selectedJoinCharacterId.set('');
    this.isPreparingJoin.set(false);
    this.joinSessionError.set('');
  }

  confirmJoinSession(): void {
    const session = this.joinTargetSession();
    const currentUser = this.auth.currentUser();
    const selectedCharacterId = this.selectedJoinCharacterId();

    if (!session || !currentUser || !selectedCharacterId) {
      this.joinSessionError.set('Choose one of your characters before joining the session.');
      return;
    }

    this.joiningSessionId.set(session.id);
    this.joinSessionError.set('');

    const payload: Partial<Player> = {
      userId: currentUser.id,
      sessionId: session.id,
      characterId: selectedCharacterId,
      companions: [],
    };

    this.gamelayerService.joinSession(session.id, payload).subscribe({
      next: () => {
        this.joiningSessionId.set(null);
        this.closeJoinSession();
        this.getSessions();
      },
      error: (error) => {
        console.error('Error joining session:', error);
        this.joinSessionError.set('The session could not be joined right now.');
        this.joiningSessionId.set(null);
      },
    });
  }

  canJoin(session: Session): boolean {
    return (
      this.auth.hasRole(UserRole.Player) &&
      !this.hasJoined(session) &&
      !this.isFull(session)
    );
  }

  goToCharacterCreation(): void {
    const session = this.joinTargetSession();
    if (!session) {
      return;
    }

    void this.router.navigate(['/characters/create'], {
      queryParams: {
        sessionId: session.id,
        sessionName: session.name,
        universeId: session.universeId,
        universeName: session.universeName,
        campaignId: session.campaignId,
        campaignName: session.campaignName,
      },
    });

    this.closeJoinSession();
  }
}
